/**
 * Recover from a stale service-worker / hashed-chunk mismatch after a deploy.
 *
 * A normal `location.reload()` and `location.href = '/'` are still intercepted
 * by the controlling SW, which is why only a browser hard-refresh appeared to
 * work. Activating a waiting SW (or unregistering + dropping Cache Storage)
 * is the in-app equivalent.
 */

const AUTO_RECOVERY_KEY = 'riftbound.chunk-retry';
const SW_RESET_KEY = 'riftbound.sw-reset';
const SKIP_WAITING_TIMEOUT_MS = 2000;

let reloadInFlight = false;

/** Test-only: `reloadToFreshApp` latches until the page unloads. */
export function resetPwaReloadForTests(): void {
  reloadInFlight = false;
}

function errorMessage(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return String(error);
}

/** True when the failure is the classic post-deploy missing-chunk / MIME mix-up. */
export function isStaleDeployError(error: unknown): boolean {
  const message = errorMessage(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Failed to load module script') ||
    message.includes('Expected a JavaScript-or-Wasm module script') ||
    message.includes('ChunkLoadError') ||
    /Loading chunk .* failed/.test(message)
  );
}

function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* sessionStorage unavailable */
  }
}

function clearFlag(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* sessionStorage unavailable */
  }
}

export function hasAttemptedAutoRecovery(): boolean {
  return readFlag(AUTO_RECOVERY_KEY);
}

export function markAutoRecoverAttempt(): void {
  writeFlag(AUTO_RECOVERY_KEY);
}

export function clearAutoRecoverAttempt(): void {
  clearFlag(AUTO_RECOVERY_KEY);
}

/**
 * Second half of a cache-reset reload. Unregister does not take effect until
 * the next navigation, so the first load after reset is still SW-controlled;
 * reload once more before mounting React.
 *
 * @returns true if a reload was triggered (caller should not mount the app)
 */
export function consumePendingSwResetReload(): boolean {
  if (!readFlag(SW_RESET_KEY)) return false;
  clearFlag(SW_RESET_KEY);
  window.location.reload();
  return true;
}

async function activateWaitingServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const waiting = registration?.waiting;
  if (!waiting) {
    try {
      await registration?.update();
    } catch {
      /* ignore stale registration / aborted */
    }
    return;
  }

  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onChange);
      resolve();
    };
    const onChange = () => finish();
    navigator.serviceWorker.addEventListener('controllerchange', onChange);
    waiting.postMessage({ type: 'SKIP_WAITING' });
    window.setTimeout(finish, SKIP_WAITING_TIMEOUT_MS);
  });
}

async function unregisterAndClearCaches(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

export type FreshAppReloadMode = 'update' | 'reset';

/**
 * `update` — ask a waiting SW to take control, then reload (normal PWA update).
 * `reset` — unregister every SW and drop Cache Storage, then two-phase reload
 * (hard-refresh equivalent; needed when the waiting-SW path already failed).
 */
export async function reloadToFreshApp(mode: FreshAppReloadMode = 'update'): Promise<void> {
  if (reloadInFlight) return;
  reloadInFlight = true;

  try {
    if (mode === 'reset') {
      writeFlag(SW_RESET_KEY);
      await unregisterAndClearCaches();
    } else {
      await activateWaitingServiceWorker();
    }
  } catch {
    /* still reload */
  }

  window.location.reload();
}

/** First stale-chunk failure activates the waiting SW; a second one resets caches. */
export async function recoverFromStaleDeploy(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
    return false;
  }
  const mode: FreshAppReloadMode = hasAttemptedAutoRecovery() ? 'reset' : 'update';
  markAutoRecoverAttempt();
  await reloadToFreshApp(mode);
  return true;
}
